import { useMemo } from 'react';

import useChartOptions from '@/components/Chart/hooks/options/useChartOptions';
import useSpotOptions from '@/components/Chart/hooks/options/useSpotOptions';
import useChartSeries from '@/components/Chart/hooks/useChartSeries';
import type { SymbolKey } from '@/entities/symbol';
import { DATAHUB_DEFAULT_GROUP } from '@/features/DataHub/constants/const';
import { useExportSettings } from '@/features/DataHub/modules/Export/context/exportContexts';
import { getXAxisRangeStrings } from '@/features/DataHub/utils/getXAxisRangeStrings';
import { useAppSelector } from '@/hooks/redux-hooks';
import type { TemplateChartSeries } from '@/types/charts';

const useSpotChartData = (symbols: SymbolKey[], symbolLimit?: number) => {
  const { exportSettings } = useExportSettings();
  const seriesColors = useAppSelector(state => state.export.seriesColors);

  const templateChartSeries: TemplateChartSeries[] = useMemo(() => {
    return Array.from(symbols)
      .slice(0, symbolLimit || 6)
      .map((symbol, index) => ({
        settings: {
          type: 'line' as const,
          ...(seriesColors?.[index] && { color: seriesColors[index] })
        },
        symbol: {
          group: DATAHUB_DEFAULT_GROUP,
          key: {
            Code: symbol.Code,
            PeriodType: symbol.PeriodType,
            Period: symbol.Period,
            TimeRef: symbol.TimeRef
          }
        }
      }));
  }, [symbols, symbolLimit, seriesColors]);

  const { loading, error, chartSeries } = useChartSeries(templateChartSeries, true);

  const effectiveChartSeries = useMemo(() => {
    if (
      exportSettings.dateRangeType !== 'range' ||
      exportSettings.period !== 'v' ||
      !chartSeries?.length
    )
      return chartSeries;

    const n = Number(exportSettings.periodNumber);
    if (!n || n <= 0) return chartSeries;

    return chartSeries.map(series => ({ ...series, data: series.data.slice(-n) }));
  }, [
    chartSeries,
    exportSettings.dateRangeType,
    exportSettings.period,
    exportSettings.periodNumber
  ]);

  const { createOptions } = useSpotOptions();
  const { options: baseOptions } = useChartOptions(effectiveChartSeries, createOptions);
  const { xAxisMin, xAxisMax } = getXAxisRangeStrings(exportSettings);

  return { loading, error, chartSeries: effectiveChartSeries, baseOptions, xAxisMin, xAxisMax };
};

export default useSpotChartData;
